
import React, { useState, useEffect } from 'react';
import { 
    MessageSquare, Trash2, CheckCircle, Clock, 
    User, Mail, Globe, MapPin, Search, Filter,
    ChevronRight, X, AlertCircle, ShieldCheck, 
    Monitor, Smartphone, Laptop, Loader2, Sparkles,
    Lightbulb, Bug, Info, Database, RefreshCw
} from 'lucide-react';
import { Feedback } from '../../types';
import { StorageService } from '../../services/storageService';
import { PageLoader } from '../../components/PageLoader';

export const FeedbackManager: React.FC = () => {
    const [feedback, setFeedback] = useState<Feedback[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState<string>('All');
    const [status, setStatus] = useState<{type: 'success' | 'error', text: string} | null>(null);

    const loadFeedback = async () => {
        setIsLoading(true);
        try {
            const res = await fetch('/practice-exams/api/admin/feedback', {
                headers: StorageService.getAuthHeader()
            });
            if (res.ok) {
                const data = await res.json();
                setFeedback(data);
            }
        } catch (e) {
            setStatus({ type: 'error', text: 'Registry Connection Failure' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { loadFeedback(); }, []);

    const handleDelete = async (id: string) => {
        if (!confirm('Permanently purge this insight?')) return;
        try {
            const res = await fetch(`/practice-exams/api/admin/feedback/${id}`, {
                method: 'DELETE',
                headers: StorageService.getAuthHeader()
            });
            if (res.ok) {
                setFeedback(prev => prev.filter(f => f._id !== id));
                setStatus({ type: 'success', text: 'Insight purged from registry.' });
            }
        } catch (e) {
            setStatus({ type: 'error', text: 'Purge protocol failed.' });
        }
    };

    const filtered = feedback.filter(f => {
        const matchesSearch = (f.userName || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                             (f.message || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = filterCategory === 'All' || f.category === filterCategory;
        return matchesSearch && matchesCategory;
    });

    const stats = {
        bugs: feedback.filter(f => f.category === 'Bug Report').length,
        features: feedback.filter(f => f.category === 'Feature Request').length,
        total: feedback.length
    };

    const getCategoryIcon = (cat: string) => {
        switch(cat) {
            case 'Bug Report': return <Bug size={18} className="text-rose-500" />;
            case 'Feature Request': return <Lightbulb size={18} className="text-amber-500" />;
            case 'Content Correction': return <Info size={18} className="text-indigo-500" />;
            default: return <MessageSquare size={18} className="text-medical-500" />;
        }
    };

    return (
        <div className="space-y-8 animate-fade-in pb-24 text-slate-900 max-w-7xl mx-auto px-4 md:px-0">
            <div className="flex flex-col lg:flex-row justify-between items-stretch gap-6 bg-slate-950 text-white p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden group border border-white/10">
                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:rotate-12 transition-transform duration-1000 pointer-events-none"><MessageSquare size={180}/></div>
                <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-3 bg-indigo-600 rounded-2xl shadow-xl"><Database size={24}/></div>
                        <h2 className="text-3xl font-black tracking-tight">Feedback Intel</h2>
                    </div>
                    <p className="text-slate-400 font-medium text-sm md:text-lg max-w-2xl leading-relaxed">
                        Direct communication channel from the clinical edge. Audit bug reports and evaluate proposed roadmap enhancements.
                    </p>
                </div>
            </div>

            {status && (
                <div className={`p-4 rounded-2xl flex items-center justify-between animate-pop border-2 shadow-xl ${status.type === 'error' ? 'bg-rose-50 border-rose-100 text-rose-700' : 'bg-emerald-50 border-emerald-100 text-emerald-700'}`}>
                    <div className="flex items-center gap-3 ml-2">
                        {status.type === 'error' ? <AlertCircle size={18}/> : <CheckCircle size={18}/>}
                        <span className="font-black text-xs tracking-widest">{status.text}</span>
                    </div>
                    <button onClick={() => setStatus(null)} className="p-2 hover:bg-black/5 rounded-xl transition-all"><X size={16}/></button>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard label="Critical Bugs" val={stats.bugs.toString()} icon={Bug} color="rose" />
                <TargetCard label="Feature Roadmap" val={stats.features.toString()} icon={Lightbulb} color="amber" />
                <StatCard label="Total Insights" val={stats.total.toString()} icon={MessageSquare} color="medical" />
            </div>

            <div className="bg-white rounded-[3rem] border border-slate-200 overflow-hidden flex flex-col shadow-sm min-h-[600px]">
                <div className="p-6 bg-slate-50 border-b border-slate-100 flex flex-col md:flex-row items-center gap-4">
                    <div className="relative flex-1 w-full group">
                        <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-400 transition-colors" size={20} />
                        <input 
                            type="text" 
                            placeholder={`Search candidate narrative...`} 
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full pl-14 pr-6 py-4 bg-white border border-slate-200 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-500/5 transition-all shadow-inner font-bold"
                        />
                    </div>
                    <div className="flex bg-white p-1 rounded-2xl border border-slate-200 shadow-sm">
                        {['All', 'Bug Report', 'Feature Request', 'Content Correction', 'General Feedback'].map(cat => (
                            <button 
                                key={cat}
                                onClick={() => setFilterCategory(cat)}
                                className={`px-4 py-2.5 rounded-xl text-[9px] font-black tracking-widest transition-all whitespace-nowrap ${filterCategory === cat ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'}`}
                            >
                                {cat === 'General Feedback' ? 'General' : cat}
                            </button>
                        ))}
                    </div>
                    <button onClick={loadFeedback} className="p-4 text-slate-400 hover:text-indigo-600 transition-all"><RefreshCw size={20}/></button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 custom-scrollbar bg-slate-50/20">
                    {isLoading ? (
                        <PageLoader compact message="Establishing Registry Pulse..." />
                    ) : filtered.length === 0 ? (
                        <div className="py-32 flex flex-col items-center justify-center text-center grayscale opacity-20 gap-4">
                            <ShieldCheck size={80} strokeWidth={1} />
                            <p className="font-black text-sm tracking-widest">Registry Log Clear</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {filtered.map(item => (
                                <div key={item._id} className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm hover:shadow-xl hover:border-indigo-400 transition-all group flex flex-col">
                                    <div className="flex justify-between items-start mb-6">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2.5 bg-slate-50 rounded-xl shadow-inner group-hover:scale-110 transition-transform">
                                                {getCategoryIcon(item.category)}
                                            </div>
                                            <div>
                                                <h4 className="font-black text-xs tracking-tight text-slate-800">{item.category}</h4>
                                                <p className="text-[8px] font-black text-slate-400 tracking-widest mt-0.5">{new Date(item.createdAt).toLocaleString()}</p>
                                            </div>
                                        </div>
                                        <button onClick={() => handleDelete(item._id)} className="p-2 text-slate-200 hover:text-rose-500 transition-all opacity-0 group-hover:opacity-100"><Trash2 size={16}/></button>
                                    </div>
                                    
                                    <p className="text-sm font-medium text-slate-600 leading-relaxed mb-8 flex-1 whitespace-pre-wrap break-words">"{item.message}"</p>
                                    
                                    <div className="pt-6 border-t border-slate-50 grid grid-cols-2 gap-4">
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <User size={12} className="text-indigo-400" />
                                                <span className="text-[9px] font-black text-slate-400">Identity</span>
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[10px] font-black text-slate-800 truncate">{item.userName || 'Anonymous'}</p>
                                                <p className="text-[8px] font-bold text-slate-400 truncate">{item.userEmail}</p>
                                            </div>
                                        </div>
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <Smartphone size={12} className="text-medical-400" />
                                                <span className="text-[9px] font-black text-slate-400">Context</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="px-2 py-0.5 bg-slate-100 rounded text-[7px] font-black text-slate-500">{item.state || 'GLB'}</div>
                                                <div className="px-2 py-0.5 bg-indigo-50 rounded text-[7px] font-black text-indigo-600">{item.certId || 'CNA'}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {item.metadata && (
                                        <div className="mt-6 p-4 bg-slate-900 rounded-2xl flex items-center justify-between shadow-2xl">
                                            <div className="flex items-center gap-3">
                                                <Monitor size={14} className="text-indigo-400" />
                                                <p className="text-[8px] font-mono text-indigo-200 truncate max-w-[200px]">{item.metadata.userAgent}</p>
                                            </div>
                                            <div className="flex gap-1">
                                                <div className="w-1 h-1 rounded-full bg-emerald-500"></div>
                                                <div className="h-1 w-1 rounded-full bg-emerald-500 opacity-40"></div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ label, val, icon: Icon, color }: any) => (
    <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm flex items-center justify-between group hover:border-indigo-500 transition-all">
        <div>
            <p className="text-[9px] font-black text-slate-400 tracking-widest mb-1">{label}</p>
            <p className="text-3xl font-black text-slate-900 tabular-nums tracking-tighter">{val}</p>
        </div>
        <div className={`p-4 rounded-2xl shadow-xl transition-all group-hover:scale-110 group-hover:rotate-6 ${color === 'rose' ? 'bg-rose-50 text-rose-600' : 'bg-medical-50 text-medical-600'}`}>
            <Icon size={28}/>
        </div>
    </div>
);

const TargetCard = ({ label, val, icon: Icon, color }: any) => (
    <div className="bg-slate-900 p-8 rounded-[2.5rem] shadow-2xl flex items-center justify-between group hover:shadow-indigo-500/10 transition-all relative overflow-hidden">
        <div className="absolute top-0 right-0 p-4 opacity-5 rotate-12 transition-transform group-hover:rotate-0 duration-700"><Icon size={80} className="text-white"/></div>
        <div className="relative z-10">
            <p className="text-[9px] font-black text-slate-500 tracking-widest mb-1">{label}</p>
            <p className="text-3xl font-black text-white tabular-nums tracking-tighter">{val}</p>
        </div>
        <div className={`relative z-10 p-4 rounded-2xl shadow-xl transition-all group-hover:scale-110 bg-amber-500 text-white`}>
            <Icon size={28}/>
        </div>
    </div>
);
